import { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router";
import { DialogContent, Grid } from "@mui/material";
import axios from "axios";
import BASE_URL from "../../../constants/baseUrls";
import { LocaleContext } from "../../../routing/LangRouter";
import { useTranslation } from "react-i18next";
import { ItineraryContext } from "../../../constants/layouts/itinerary-layout";

//import utils
import { convertToJalali } from "../../../utils/date";
import { numberWithCommas } from "../../../utils/utility";

//import customized hooks
import useCityList from "../../../hooks/useCityList";

//import general components
import Loading from "../loading/loading";
import BaseDialog from "../base-dialog/base-dialog";
import EmptyOrErrorState from "../empty-or-error-state/empty-or-error-state";

//import images and icons
import noImage from "../../../assets/imgs/location.png";
import { Warning2 } from "iconsax-react";

//import styles
import "./without-modal.scss";

function WithoutDestinationModal({ open, setOpen, setRequestFlag }) {
    //general variables
    const navigate = useNavigate();
    const location = useLocation();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const accessToken = localStorage.getItem("accessToken");
    const { routes, setRoutes } = useContext(ItineraryContext);

    //sessionStorage variables
    const domesticFlightInformation = JSON.parse(sessionStorage.getItem("domesticFlightInformation"));
    const intFlightInformation = JSON.parse(sessionStorage.getItem("intFlightInformation"));
    const hotelInformation = JSON.parse(sessionStorage.getItem("hotelInformation"));
    const itineraryInformation = JSON.parse(sessionStorage.getItem("itineraryInformation"));
    const { isTicket, isAccommodation, isItinerary } = JSON.parse(sessionStorage.getItem("generalInformation")) || {};
    const favorites = JSON.parse(sessionStorage.getItem("favorites"));

    const [information, setInformation] = useState();
    useEffect(() => {
        if (isTicket) {
            setInformation(domesticFlightInformation);
            setInformation(intFlightInformation);
        }
        if (!isTicket && isAccommodation) {
            setInformation(domesticFlightInformation);
            setInformation(intFlightInformation);
        }
        if (!isTicket && !isAccommodation) {
            setInformation(itineraryInformation);
        }
    }, []);

    const date =
        locale === "fa"
            ? `${convertToJalali(information?.departureDate)} ${t("to-2")} ${convertToJalali(information?.returnDate)}`
            : `${information?.departureDate} ${t("to-2")} ${information?.returnDate}`;

    //handle close modal
    const handleClose = () => {
        setOpen(false);
        navigate(`/${locale}`, { state: { scrollToTravelForm: true } });
    };

    //without destination request
    const [loadingCityRecommender, setLoadingCityRecommender] = useState(false);
    const [cityRecommenderList, setCityRecommenderList] = useState();
    const [error, setError] = useState(false);
    const getCityRecommenderList = async () => {
        try {
            setLoadingCityRecommender(true);
            setError(false);
            const data = {
                favourites: favorites,
                timings: {
                    start_datetime: information.departureDate + "T" + itineraryInformation.firstDayTime,
                    end_datetime: information.returnDate + "T" + itineraryInformation.lastDayTime,
                    custom_start_time: itineraryInformation.startTime,
                    custom_end_time: itineraryInformation.endTime,
                },
                travelers: {
                    adults_count: information?.personCounter.adult,
                    children_count: information?.personCounter.child,
                    infants_count: information?.personCounter.infant,
                },
                user_id: accessToken ? JSON.parse(localStorage.getItem("userInformation")).id : 0,
                from_city_id: information.departure.id,
            };

            const response = await axios.post(`${BASE_URL}/itinerary/city_recommender/`, data, {
                params: {
                    is_cake: false,
                },
            });
            setCityRecommenderList(
                response.data.data
                    .sort((a, b) => b?.total_score - a?.total_score)
                    .filter(item => item.city_id !== domesticFlightInformation.departure.id),
            );
        } catch {
            setError(true);
        } finally {
            setLoadingCityRecommender(false);
        }
    };

    useEffect(() => {
        if (!information?.destination && !!information?.departureDate && !!information?.returnDate) {
            getCityRecommenderList();
        }
    }, [information]);

    //request city list. inputs:foreign, all, flight, accommodation, bus
    const { getCities } = useCityList(false, true, false, false, true, false); //when the (all) parameter is set to true, it ignores the value of foreign

    //handle select destination
    const handleDestinationChange = city => {
        getCities(city.city_name).then(cityList => {
            const findCity = cityList.find(cityItem => cityItem.id === city.city_id);

            domesticFlightInformation.destination = findCity;
            sessionStorage.setItem("domesticFlightInformation", JSON.stringify(domesticFlightInformation));

            intFlightInformation.destination = findCity;
            sessionStorage.setItem("intFlightInformation", JSON.stringify(intFlightInformation));

            hotelInformation.destination = findCity;
            sessionStorage.setItem("hotelInformation", JSON.stringify(hotelInformation));
            itineraryInformation.destination = findCity;
            sessionStorage.setItem("itineraryInformation", JSON.stringify(itineraryInformation));

            //skip the hotel step in the flow of itinerary when the city has flight but doesn't have accommodation
            if (findCity?.flight && !findCity?.accommodation) {
                const newGeneralInformation = {
                    isTicket,
                    isAccommodation: false,
                    isItinerary,
                };
                sessionStorage.setItem("generalInformation", JSON.stringify(newGeneralInformation));
            }

            if (!findCity?.flight) {
                if (findCity?.accommodation) {//skip the flight step in the flow of itinerary when the city doesn't have flight but have accommodation 
                    const newGeneralInformation = {
                        isTicket: false,
                        isAccommodation,
                        isItinerary,
                    };
                    sessionStorage.setItem("generalInformation", JSON.stringify(newGeneralInformation));
                    setRoutes([`/${locale}/itinerary/favorites`, `/${locale}/itinerary/hotel`]);
                    sessionStorage.setItem("visitedRoutes", JSON.stringify([`/${locale}/itinerary/favorites`, `/${locale}/itinerary/hotel`]));
                    navigate(`/${locale}/itinerary/hotel`);
                } else {//skip both flight and accommodation steps when the city doesn't have flight and accommodation 
                    const newGeneralInformation = {
                        isTicket: false,
                        isAccommodation: false,
                        isItinerary,
                    };
                    sessionStorage.setItem("generalInformation", JSON.stringify(newGeneralInformation));
                    setRoutes([`/${locale}/itinerary/favorites`,`/${locale}/itinerary/itinerary-page`]);
                    sessionStorage.setItem("visitedRoutes", JSON.stringify([`/${locale}/itinerary/favorites`,`/${locale}/itinerary/itinerary-page`]));
                    navigate(`/${locale}/itinerary/itinerary-page`);
                }
            }

            //skip both flight and accommodation steps when the user doesn't select flight and goes to the hotel page and the selected city doesn't have accommodation 
            if (!isTicket && !findCity?.accommodation) {
                const newGeneralInformation = {
                    isTicket,
                    isAccommodation: false,
                    isItinerary,
                };
                sessionStorage.setItem("generalInformation", JSON.stringify(newGeneralInformation));
                setRoutes([ `/${locale}/itinerary/favorites`, `/${locale}/itinerary/itinerary-page`]);
                sessionStorage.setItem(
                    "visitedRoutes",
                    JSON.stringify([
                        `/${locale}/itinerary/favorites`,
                        `/${locale}/itinerary/itinerary-page`,
                    ]),
                );
                navigate(`/${locale}/itinerary/itinerary-page`);
            }

            //when the selected country is international
            if (findCity?.country.id !== 1) {
                const newGeneralInformation = {
                    isTicket,
                    isAccommodation,
                    isItinerary,
                    isHotelInternational: true,
                    isFlightInternational: true,
                };
                sessionStorage.setItem("generalInformation", JSON.stringify(newGeneralInformation));
                const { isFlightInternational, isHotelInternational } = JSON.parse(sessionStorage.getItem("generalInformation")) || {};

                if (isFlightInternational) {
                    setRoutes([...routes, `/${locale}/itinerary/international-flight`]);
                    sessionStorage.setItem(
                        "visitedRoutes",
                        JSON.stringify([...routes, `/${locale}/itinerary/favorites`, `/${locale}/itinerary/international-flight`]),
                    );
                    navigate(`/${locale}/itinerary/international-flight`);
                }

                //when the modal opens in the hotel page for when the user just select I want accommodation
                if (!isTicket && isHotelInternational) {
                    setRoutes([...routes, `/${locale}/itinerary/hotel`]);
                    sessionStorage.setItem(
                        "visitedRoutes",
                        JSON.stringify([...routes, `/${locale}/itinerary/favorites`, `/${locale}/itinerary/hotel`]),
                    );
                    navigate(`/${locale}/itinerary/hotel`);
                }
            }
            setOpen(false);

            if (location.pathname !== `/${locale}/itinerary/hotel`) {
                setRequestFlag(true);
            }
        });
    };

    return (
        <BaseDialog title={`${t("suggesteDestinations")} ${date}`} open={open} handleClose={handleClose} maxWidth="lg" top="0" icon="back">
            {loadingCityRecommender && <Loading />}
            <DialogContent dividers={true}>
                <Grid
                    className="without-modal"
                    container
                    item
                    xs={12}
                    rowGap={2}
                    columnGap={{ xs: 1, md: 5 }}
                    justifyContent={{ xs: "space-between", md: "flex-start" }}
                >
                    {error ? (
                        <Grid container item xs={12} justifyContent="center">
                            <EmptyOrErrorState
                                title={t("errorMessage")}
                                icon={<Warning2 size="32" color="var(--red-color)" variant="Bold" />}
                                actionLabel={t("retry")}
                                handleClick={getCityRecommenderList}
                            />
                        </Grid>
                    ) : cityRecommenderList?.length ? (
                        cityRecommenderList?.map((cityRecommenderItem, index) => {
                            return (
                                <Grid
                                    key={cityRecommenderItem?.city_id}
                                    className="without-modal__city-recommender destination"
                                    container
                                    item
                                    xs={12}
                                    md={2.5}
                                    alignItems="flex-start"
                                    sx={{ backgroundImage: `url(${cityRecommenderItem.image || noImage}  )` }}
                                    onClick={() => handleDestinationChange(cityRecommenderItem)}
                                >
                                    <Grid container item xs={12}>
                                        <span className="without-modal__city-name">
                                            {index + 1}- {locale === "en" ? cityRecommenderItem.city_name_english : cityRecommenderItem.city_name}
                                        </span>
                                    </Grid>

                                    <Grid className="without-modal__prices blur" container item xs={12} rowGap={0.5} alignSelf="flex-end">
                                        <Grid container item xs={12} justifyContent="space-between">
                                            <span>{t("departurePrice")}:</span>
                                            <span>
                                                {t("from")} {numberWithCommas(cityRecommenderItem?.departure_flight_price)} {t("currency")}
                                            </span>
                                        </Grid>
                                        <Grid container item xs={12} justifyContent="space-between">
                                            <span>{t("destinationPrice")}:</span>
                                            <span>
                                                {t("from")} {numberWithCommas(cityRecommenderItem?.return_flight_price)} {t("currency")}
                                            </span>
                                        </Grid>
                                        <Grid container item xs={12} justifyContent="space-between">
                                            <span>{t("hotelPrice")}:</span>
                                            <span>
                                                {t("from")} {numberWithCommas(cityRecommenderItem?.hotel_price)} {t("currency")}
                                            </span>
                                        </Grid>
                                        <Grid container item xs={12} justifyContent="space-between">
                                            <span>{t("total")}:</span>
                                            <span>
                                                {numberWithCommas(cityRecommenderItem?.total_price)} {t("currency")}
                                            </span>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            );
                        })
                    ) : (
                        <Grid
                            className="without-modal__loading-text"
                            container
                            item
                            xs={12}
                            rowGap={2}
                            direction="column"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <span> {t("lookingForBest")}</span>
                            <span>{t("thisProcessMayTakeTime")}</span>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
        </BaseDialog>
    );
}

export default WithoutDestinationModal;
